'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
    id: number;
    step: number;
    icon: string;
    title: string;
    description: string;
}

const LearningProcess: React.FC = () => {
    const steps: ProcessStep[] = [
        {
            id: 1,
            step: 1,
            icon: 'UserCircleIcon',
            title: 'Create Your Profile',
            description: 'Tell us about your goals, interests, and current skill level to get personalized recommendations.'
        },
        {
            id: 2,
            step: 2,
            icon: 'MapIcon',
            title: 'Choose Learning Path',
            description: 'Select from curated paths or create your own custom journey based on your career aspirations.'
        },
        {
            id: 3,
            step: 3,
            icon: 'BookOpenIcon',
            title: 'Learn & Practice',
            description: 'Engage with interactive lessons, hands-on projects, and real-world case studies.'
        },
        {
            id: 4,
            step: 4,
            icon: 'CheckBadgeIcon',
            title: 'Get Certified',
            description: 'Earn industry-recognized certificates and showcase your achievements to employers.'
        }
    ];

    return (
        <section className="py-16 bg-background">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
                        How AstikLearn Helps You Succeed
                    </h2>
                    <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                        A simple, proven process that takes you from beginner to job-ready professional
                    </p>
                </div>

                <div className="relative">
                    {/* Connection Line */}
                    <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-border -translate-y-1/2" />

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
                        {steps.map((step, index) => (
                            <div key={step.id} className="relative">
                                <div className="glass glass-border rounded-lg p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative z-10 bg-surface">
                                    {/* Step Number Badge */}
                                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                                        {step.step}
                                    </div>

                                    <div className="mb-4 text-primary mt-4">
                                        <Icon name={step.icon} size={48} variant="solid" />
                                    </div>

                                    <h3 className="text-xl font-semibold text-text-primary mb-3">
                                        {step.title}
                                    </h3>

                                    <p className="text-text-secondary leading-relaxed">
                                        {step.description}
                                    </p>
                                </div>

                                {/* Arrow for desktop */}
                                {index < steps.length - 1 && (
                                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                                        <Icon name="ArrowRightIcon" size={32} variant="solid" className="text-primary" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LearningProcess;